import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Sparkles, CalendarDays } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage, userInitials } from "@wa/ui";
import { APP_VERSION } from "@wa/core/version";
import { useI18n, useT } from "../../i18n/I18nProvider";
import { ADMIN_URL, type AccountUser } from "../../lib/auth";
import { fmtDate } from "../../lib/format";
import { AppBackground } from "../../components/AppBackground";
import { AccountHeader, type AdminLink } from "../header";
import { TabButton } from "../TabButton";
import type { TabKey } from "../tabs";

/**
 * The chrome of Husly's own hub: the top bar, the greeting, the side menu and
 * the animated content slot.
 *
 * Lifted out of `AccountLayout`, which held the data, the tab rules AND this
 * markup. The layout still decides everything that may appear - who the visitor
 * is, which tabs they may open, which one is active - and hands it down as
 * props; this file only draws it.
 *
 * Only ever drawn for a signed-in person on Husly's own host. The operator's
 * client portal and a staff preview of it wear `PortalShell` instead, so there
 * is no preview banner and no brand switch in here.
 */
export function HubShell({
  user,
  adminLink,
  crmLink,
  active,
  portalTabs,
  accountTabs,
  referralEnabled,
  onGo,
  onInvite,
  onLogout,
  children,
}: {
  user: AccountUser;
  adminLink: AdminLink;
  /** The staff CRM, for somebody the CRM would let in. */
  crmLink: boolean;
  active: TabKey;
  /** The client's service tabs this visit may open. */
  portalTabs: { key: TabKey; icon: LucideIcon }[];
  /** The account tabs to draw, already filtered for this audience. */
  accountTabs: { key: TabKey; icon: LucideIcon }[];
  referralEnabled: boolean;
  onGo: (tab: TabKey) => void;
  onInvite: () => void;
  onLogout: () => void;
  /** The screen behind the active tab. */
  children: ReactNode;
}) {
  const t = useT();
  const { locale } = useI18n();
  const initials = userInitials(user.name, user.email);

  return (
    <div className="relative min-h-dvh">
      {/* `fixed` -> the background pins to the viewport; it neither moves nor stretches
         while a long hub scrolls (previously `absolute` stretched to the full height of
         the `min-h-dvh` parent). */}
      <AppBackground spec={{ intensity: 28 }} fixed />

      <AccountHeader
        user={user}
        portalBrand={false}
        adminLink={adminLink}
        crmLink={crmLink}
        active={active}
        portalTabs={portalTabs}
        menuTabs={accountTabs}
        showInvite={referralEnabled}
        onGo={onGo}
        onInvite={onInvite}
        onLogout={onLogout}
      />

      {/* `relative z-10` lifts the content above the BgEffect background (z-0,
         AppBackground). Without it the content sits UNDER the background after the
         animation (the motion.div transform disappears -> the stacking context is lost
         -> the mesh covers the content). */}
      <main className="relative z-10 mx-auto max-w-6xl px-4 py-8 md:py-10">
        {/* ── Greeting (avatar + name + email) ────────────────────────────
            A plain row rather than a card. The chrome it used to wear - a
            border, a tinted surface and a backdrop blur - drew a box around a
            greeting, which is not something the reader acts on. The credit
            balance and the sign-out button moved out with it: the header's
            account menu is the one place for signing out, and the wallet has
            its own card on the overview below. */}
        <div className="mb-6 flex flex-wrap items-center gap-4">
          <Avatar className="size-12 shrink-0">
            {user.avatarUrl ? <AvatarImage src={user.avatarUrl} alt="" /> : null}
            <AvatarFallback className="bg-primary/15 text-primary text-base font-bold uppercase">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-xl font-bold tracking-tight md:text-2xl">
              {t("app.greeting", { name: user.name || user.email })}
            </h1>
            <p className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Mail className="size-3.5" /> {user.email}
              </span>
              {/* How long they have been here. It used to be one line inside the
                  overview's own greeting card, which said the name and the email
                  over again; that card is gone and this is the fact it held that
                  was not already on the screen. */}
              {user.createdAt && (
                <span className="inline-flex items-center gap-1.5">
                  <CalendarDays className="size-3.5" />
                  {t("overview.memberSince", { date: fmtDate(user.createdAt, locale) })}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="grid items-start gap-6 lg:grid-cols-[240px_1fr]">
          {/* ── Side menu, and it holds a different list at each width ─────
              From `lg` up it is the sticky column of ACCOUNT tabs; the services
              live in the header row, once. Under `lg` the header row is gone,
              so this becomes the row of SERVICE tabs under the banner and the
              account tabs move into the hamburger. Every tab is in exactly one
              visible place at any width - the two lists never both appear.
              Wrapping rather than scrolling sideways: a strip with its
              scrollbar hidden gives no hint that a tab is off the edge, and
              "Lepingud" being invisible on a phone is the same bug as it not
              existing. */}
          <nav className="flex flex-wrap gap-1 lg:sticky lg:top-24 lg:flex-col lg:flex-nowrap lg:gap-0.5">
            {accountTabs.map(({ key, icon: Icon }) => (
              <TabButton key={key} tab={key} Icon={Icon} active={active === key} label={t(`tabs.${key}`)} className="max-lg:hidden" onClick={() => onGo(key)} />
            ))}

            {/* "Get free credit" - opens the invite modal (not a tab). Highlighted in
                the primary colour; shown only while the programme is on. */}
            {referralEnabled && (
              <button
                onClick={onInvite}
                className="flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary/10 max-lg:hidden"
              >
                <Sparkles className="size-4" />
                <span>{t("inviteModal.menuLabel")}</span>
              </button>
            )}

            {/* The services, under `lg` only. Above it the header row has them,
                and drawing both is what put every link on the screen twice. */}
            {portalTabs.map(({ key, icon: Icon }) => (
              <TabButton key={key} tab={key} Icon={Icon} active={active === key} label={t(`tabs.${key}`)} className="lg:hidden" onClick={() => onGo(key)} />
            ))}

            {/* The build this account is looking at, in the corner of the menu. It links
                into the admin panel's changelog, because a version number is only useful
                if you can find out what is in it. Wide screens only - under `lg` this
                nav is the row of service tabs, and a version chip in it would read as
                one more tab. */}
            <a
              href={`${ADMIN_URL}/changelog`}
              className="mt-3 hidden px-3 font-mono text-[11px] text-muted-foreground/70 transition-colors hover:text-foreground lg:block"
            >
              v{APP_VERSION}
            </a>
          </nav>

          {/* ── Content (animated tab switch) ─────────────────────────────── */}
          <div className="min-w-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.16 }}
              >
                {children}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>
  );
}
